import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wifi, WifiOff, Laptop, Loader2, AlertTriangle, ArrowUpCircle, RotateCcw, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { InterviewRoadmap } from "./interview-roadmap";
import { RunWithChoice, cloudCanRun, cloudHoursLeft, keyCanRun, type RunWith } from "./run-with-choice";
import { CloudRunOffer } from "./cloud-run-offer";
import { OutdatedMachineNotice } from "@/components/machines/outdated-machine-notice";
import { useToast } from "@/hooks/useToast";
import { useMachines, useEnqueueJob } from "@/hooks/queries/use-mailbox";
import { useAiSettings, useStartKeyInterview } from "@/hooks/queries/use-ai-settings";
import { useCloudRunStatus, useStartCloudRun } from "@/hooks/queries/use-cloud-runs";
import { AI_PROVIDERS, type ConversationChatModelId } from "@mycompbox/shared";
import {
  useInterviews,
  useCreateInterview,
  useArchiveInterview,
} from "@/hooks/queries/use-interviews";
import {
  machineReadiness,
  machineServicesRemote,
  type JobType,
} from "@/lib/api/mailbox";
import { ApiError } from "@/lib/api/client";
import type { CheckMode } from "@/lib/api/types";
import { CHECK_MODE_OPTIONS, toCheckMode } from "@/components/teams/check-mode-card";

/**
 * Reference context carried into a fresh interview when the user chooses to
 * "start a new request from" a previous transcript (ENG-145). This is a SEED,
 * not a copy: the machine runs a brand-new interview, with the prior
 * transcript's content available as reference material in the job payload. We
 * never reconstruct/auto-generate an EpicRequest from it — the daemon reads the
 * reference and asks its own fresh questions.
 */
export interface SeedFromInterview {
  /** The completed/archived interview whose transcript seeds this one. */
  interviewId: string;
  /** Flattened transcript text to hand the formulator as reference context. */
  reference: string;
}

interface RunOnMachineDialogProps {
  children?: React.ReactNode;
  /**
   * When set, this launch seeds a NEW interview from a prior transcript
   * (ENG-145). The copy adapts and the reference is carried in the job payload.
   * Absent for the normal "start fresh" launch. Only meaningful for the default
   * `epic_request` job type.
   */
  seedFromInterview?: SeedFromInterview;
  /**
   * The kind of job to enqueue (ENG-163). Defaults to `"epic_request"` — the
   * request-formulator interview flow — so existing mounts are unaffected. Pass
   * `"start_execution"` (with a matching `payload`) to dispatch the planner or
   * orchestrator to the machine instead.
   */
  jobType?: JobType;
  /**
   * Payload for the enqueued job (ENG-163). For `start_execution` this is a
   * `StartExecutionPayload` (`{ kind: 'plan', epicRequestId }` or
   * `{ kind: 'orchestrate', epicId }`). Ignored for `epic_request`, whose
   * payload is derived from `seedFromInterview` instead.
   */
  payload?: unknown;
  /**
   * For execution dispatch (ENG-178): the target project's git remote
   * (`epic.team.gitRemote` on the Epic page / `request.team.gitRemote` on the
   * Epic Request page). When set, only machines whose daemon services that
   * remote can run the job — others are disabled with a reason and the single
   * servicing + ready machine auto-selects. `null`/absent means the project has
   * no configured repo, so remote filtering is skipped and every machine
   * qualifies (backward-compatible with pre-ENG-176 dispatch).
   */
  targetRemote?: string | null;
  /**
   * Discuss an EXISTING Epic Request (ENG-291). The new interview is linked to
   * this request at start, so the conversation updates it in place instead of
   * creating a new one. Interview flow only; resume detection is skipped (an
   * unrelated in-progress interview is not this discussion).
   */
  epicRequestId?: string;
  /**
   * ENG-350: the project's fast-mode default. A build (orchestrate) shows a
   * "Fast mode" toggle starting from this value and sends the choice.
   */
  defaultFastMode?: boolean;
  /**
   * ENG-395: the project's Build checks setting (every | smart | end). A build
   * (orchestrate) shows a picker starting from this value; the choice is sent
   * only when it differs, so the server stamps on the project default otherwise.
   */
  defaultCheckMode?: CheckMode | undefined;
  /**
   * Stay on this page after sending a plan or build, and hand the new job's id
   * to the caller instead of opening the job's page. The new look's idea and
   * blueprint pages follow the run in their own stream. Interviews still open
   * their page.
   */
  onStarted?: (jobId: string) => void;
  /**
   * ENG-390: the chat model the person picked for this conversation. Sent as
   * `model` in a new `epic_request` job's payload; ignored for execution jobs.
   */
  chatModel?: ConversationChatModelId;
  /**
   * ER-145: where the idea runs, so the Run with picker starts there (a
   * computer only when one is paired).
   */
  preferredRunWith?: RunWith;
}

/**
 * Entry point for the mailbox browser flow (ENG-123): lets the user start the
 * request-formulator interview on their paired machine, right from the
 * browser — no terminal needed (contrast with CliInstructionModal, which
 * still covers the copy-paste-a-command path for users without a paired
 * machine).
 *
 * Enqueues an `epic_request` job for the selected machine, then navigates to
 * the job's page to run the turn-based interview.
 *
 * When `seedFromInterview` is provided (ENG-145), the enqueued job carries the
 * prior transcript as reference context so the machine can run a fresh,
 * transcript-informed interview. Resume detection still applies, so seeding
 * never produces a duplicate of an already in-progress interview — the user is
 * offered the existing one to resume instead.
 */
export function RunOnMachineDialog({
  children,
  seedFromInterview,
  jobType = "epic_request",
  payload,
  targetRemote,
  epicRequestId,
  defaultFastMode = false,
  defaultCheckMode,
  onStarted,
  chatModel,
  preferredRunWith,
}: RunOnMachineDialogProps) {
  const [open, setOpen] = React.useState(false);
  // ENG-350: fast mode for this run, starting from the project's default.
  const [fastMode, setFastMode] = React.useState<boolean>(defaultFastMode);
  React.useEffect(() => {
    if (open) setFastMode(defaultFastMode);
  }, [open, defaultFastMode]);
  // ENG-395: build check cadence for this run, starting from the project's setting.
  const projectCheckMode = toCheckMode(defaultCheckMode);
  const [checkMode, setCheckMode] = React.useState<CheckMode>(projectCheckMode);
  React.useEffect(() => {
    if (open) setCheckMode(projectCheckMode);
  }, [open, projectCheckMode]);
  const [selectedMachineId, setSelectedMachineId] = React.useState<string>("");
  // Why the server refused to send the run (e.g. the project has no
  // repository). Shown in the dialog itself, not only in a passing toast.
  const [sendError, setSendError] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Execution mode (ENG-163): a `start_execution` dispatch runs the planner or
  // orchestrator on the machine. It skips the interview-only machinery
  // (lifecycle interview linkage, resume detection, section roadmap) — those
  // are meaningful only for the `epic_request` flow.
  const isExecution = jobType === "start_execution";
  // The plan/orchestrate discriminant, used only to tailor the dialog copy.
  const executionKind =
    isExecution && payload !== null && typeof payload === "object"
      ? (payload as { kind?: string }).kind
      : undefined;

  // ENG-178: remote-aware machine gating. Only execution dispatch with a
  // configured project remote filters machines; otherwise (interview flow, or a
  // project with no git remote) every machine qualifies — backward-compatible.
  const remoteFilter = isExecution ? (targetRemote ?? null) : null;

  const { data: machines, isLoading: machinesLoading } = useMachines();
  const enqueueJob = useEnqueueJob();
  const { data: aiSettings } = useAiSettings();
  const startKeyInterview = useStartKeyInterview();

  // Run with the paired computer, or the API key. Interviews can run on the
  // key on CompBox's server; a seeded restart needs the computer (the seed
  // reference is a computer-flow feature).
  const hasComputer = (machines?.length ?? 0) > 0;
  const keyReady = keyCanRun(aiSettings, isExecution ? "execution" : "interview", hasComputer) && !seedFromInterview;
  // Planning and building can also run in CompBox cloud (ENG-401): a
  // temporary machine on the person's Anthropic key, ending as a pull request.
  const cloudPayload = (isExecution && payload !== null && typeof payload === "object" ? payload : {}) as {
    epicId?: string;
    epicRequestId?: string;
  };
  const offerCloud = executionKind === "plan" || executionKind === "orchestrate";
  const cloudTarget =
    executionKind === "plan"
      ? cloudPayload.epicRequestId ? { epicRequestId: cloudPayload.epicRequestId } : {}
      : cloudPayload.epicId ? { epicId: cloudPayload.epicId } : {};
  const { data: cloudStatus } = useCloudRunStatus(cloudTarget, open && offerCloud);
  const startCloudRun = useStartCloudRun();
  const cloudReady = offerCloud && cloudCanRun(cloudStatus);
  const [runWithChoice, setRunWithChoice] = React.useState<RunWith | null>(null);
  const defaultRunWith: RunWith =
    // The idea runs in CompBox cloud: start there even when it can't run yet,
    // so the picker says why instead of quietly switching to a computer.
    preferredRunWith === "cloud" && offerCloud
      ? "cloud"
      : hasComputer
        ? "computer"
        : keyReady
          ? "key"
          : cloudReady
            ? "cloud"
            : "computer";
  const runWith: RunWith = runWithChoice ?? defaultRunWith;
  // On a key, an interview runs on CompBox's server; planning and building
  // still go to the computer, which runs Claude Code on the key.
  const onKey = runWith === "key" && !isExecution;
  const executionOnKey = runWith === "key" && isExecution;
  const onCloud = runWith === "cloud" && offerCloud;
  const activeKey =
    aiSettings?.effectiveKey === "personal" ? aiSettings.personalKey : aiSettings?.effectiveKey === "company" ? aiSettings.companyKey : null;
  const createInterview = useCreateInterview();

  // Resume detection (ENG-144): while the dialog is open, look for the caller's
  // in-progress interview so we can RECONNECT to its live job instead of
  // starting a duplicate. Only interviews that still have a driving job are
  // resumable (that's the live-turn stream we reconnect to). Disabled in
  // execution mode — resume is an interview concept.
  const { data: inProgressInterviews } = useInterviews(
    { status: "in_progress" },
    { enabled: open && !isExecution && !epicRequestId }
  );
  const resumable = isExecution || epicRequestId
    ? null
    : (inProgressInterviews?.data.find((interview) => interview.jobId) ?? null);

  // Auto-select the only machine, or the first launch-ready one, whenever the
  // machine list changes while the dialog is open.
  React.useEffect(() => {
    if (!open || !machines || machines.length === 0) return;
    setSelectedMachineId((current) => {
      if (current && machines.some((m) => m.id === current)) return current;
      // ENG-178: prefer a machine that services the target remote AND is ready,
      // then any servicing machine, then fall back to the first machine.
      const eligibleReady = machines.find(
        (m) =>
          machineServicesRemote(m, remoteFilter) &&
          machineReadiness(m, Date.now(), jobType) === "ready"
      );
      if (eligibleReady) return eligibleReady.id;
      const servicing = machines.find((m) =>
        machineServicesRemote(m, remoteFilter)
      );
      return (servicing ?? machines[0])?.id ?? "";
    });
  }, [open, machines, remoteFilter]);

  const selectedMachine = machines?.find((m) => m.id === selectedMachineId);
  // Server-derived, authoritative readiness. 'ready' is the only state that
  // permits a launch; 'runtime-not-found' and 'offline' each get their own copy.
  const readiness = selectedMachine ? machineReadiness(selectedMachine, Date.now(), jobType) : null;
  const ready = readiness === "ready";
  // ENG-178: does the selected machine service the target remote? (Always true
  // when there is no remote filter.) And does ANY machine service it?
  const selectedServicesTarget = selectedMachine
    ? machineServicesRemote(selectedMachine, remoteFilter)
    : false;
  const anyMachineServices =
    !remoteFilter ||
    (machines?.some((m) => machineServicesRemote(m, remoteFilter)) ?? false);

  // Dialog copy, tailored per mode (ENG-163). The interview flow keeps its exact
  // original wording; execution mode explains what agent will run.
  const dialogTitle = epicRequestId && !isExecution
    ? "Discuss this request"
    : onKey
    ? "Start a request"
    : isExecution
    ? "Send to your machine"
    : seedFromInterview
      ? "Start a new request from this transcript"
      : "Run on Your Machine";
  const dialogDescription = isExecution
    ? executionKind === "orchestrate"
      ? "Run the orchestrator on your paired machine to execute this epic's plan — right here in the browser, no terminal needed."
      : "Run the planner on your paired machine to turn this approved request into an epic — right here in the browser, no terminal needed."
    : epicRequestId
      ? "Talk this request through with the request formulator. It starts from what the request already says, and the request is updated in place — no duplicate is created."
    : seedFromInterview
      ? "Start a fresh interview on your paired machine, carrying the selected transcript along as reference. The formulator asks its own questions — nothing is auto-submitted."
      : "Start the request-formulator interview on your paired machine — right here in the browser, no terminal needed.";
  // Footer launch-button label. runtime-not-found copy is shared; otherwise the
  // interview flow keeps "Start Interview" (asserted by existing tests) and
  // execution mode reads "Send to Machine".
  const launchLabel =
    readiness === "runtime-not-found"
      ? "Agent Runtime Not Found"
      : readiness === "outdated"
        ? "Update Required"
        : isExecution
        ? "Send to Machine"
        : "Start Interview";
  const pendingLabel = isExecution ? "Sending..." : "Starting...";

  const handleStartOnKey = async () => {
    try {
      const response = await startKeyInterview.mutateAsync(
        epicRequestId ? { epicRequestId } : {}
      );
      setOpen(false);
      void navigate(`/mailbox/jobs/${response.data.id}`);
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Failed to start the interview");
    }
  };

  const handleStartInCloud = async () => {
    if (!cloudReady) return;
    setSendError(null);
    try {
      const job = await startCloudRun.mutateAsync({
        kind: executionKind,
        ...cloudTarget,
        // ENG-430: a cloud build carries the same Fast mode and Build checks
        // choices as a build on a computer.
        ...(executionKind === "orchestrate" ? { fastMode } : {}),
        ...(executionKind === "orchestrate" && checkMode !== projectCheckMode ? { checkMode } : {}),
      });
      setOpen(false);
      if (onStarted) onStarted(job.id);
      else void navigate(`/mailbox/jobs/${job.id}`);
    } catch (error) {
      setSendError(error instanceof ApiError ? error.message : "Couldn't start it in CompBox cloud");
    }
  };

  const handleStart = async () => {
    if (onKey) {
      await handleStartOnKey();
      return;
    }
    if (onCloud) {
      await handleStartInCloud();
      return;
    }
    if (!selectedMachineId || !ready || !selectedServicesTarget) return;
    setSendError(null);
    try {
      const job = await enqueueJob.mutateAsync({
        machineId: selectedMachineId,
        type: jobType,
        // Execution jobs (ENG-163) carry the caller-supplied StartExecutionPayload
        // verbatim. Interview jobs carry optional seed context (ENG-145) in the
        // arbitrary payload; omitted for a normal start so the "start clean" path
        // is unchanged.
        payload: isExecution
          ? payload !== null && typeof payload === "object"
            ? {
                ...payload,
                ...(executionOnKey ? { useAiKey: true } : {}),
                // ENG-350: a build always says whether to use fast mode.
                ...(executionKind === "orchestrate" ? { fastMode } : {}),
                // ENG-395: only an override is sent; otherwise the project default applies.
                ...(executionKind === "orchestrate" && checkMode !== projectCheckMode ? { checkMode } : {}),
              }
            : payload
          : {
              ...(seedFromInterview
                ? {
                    seedFromInterviewId: seedFromInterview.interviewId,
                    seedReference: seedFromInterview.reference,
                  }
                : {}),
              // ENG-390: the chosen chat model rides along on new conversations.
              ...(chatModel ? { model: chatModel } : {}),
            },
      });
      // Interview flow only: create the lifecycle Interview linked to this job AT
      // START. This is what lets the job-complete hook (ENG-142) stamp the
      // interview and snapshot its transcript — without this linkage, completion
      // linkage never happens. Best-effort: if the record can't be created we
      // still proceed into the interview (the job exists), surfacing the error
      // rather than blocking. Execution jobs have no interview to link.
      if (!isExecution) {
        try {
          await createInterview.mutateAsync({
            jobId: job.id,
            ...(epicRequestId ? { epicRequestId } : {}),
          });
        } catch (interviewError) {
          const message =
            interviewError instanceof ApiError
              ? interviewError.message
              : "Interview record could not be created";
          toast(message);
        }
      }
      setOpen(false);
      // A caller that follows the run itself keeps the person on its page.
      if (isExecution && onStarted) {
        onStarted(job.id);
        return;
      }
      // Both flows land on the job page: the interview runs its turns there, and
      // an execution job surfaces live progress via the job's linked epic.
      void navigate(`/mailbox/jobs/${job.id}`);
    } catch (error) {
      const message =
        error instanceof ApiError
          ? error.message
          : isExecution
            ? "Failed to send the job to your machine"
            : "Failed to start the interview";
      if (isExecution) setSendError(message);
      toast(message);
    }
  };

  // Resume: reconnect to an existing in-progress interview's live job — never
  // create a second interview for the same conversation.
  const handleResume = () => {
    if (!resumable?.jobId) return;
    setOpen(false);
    void navigate(`/mailbox/jobs/${resumable.jobId}`);
  };

  // Discard: archive a stuck in-progress interview (e.g. its driving job died)
  // so resume detection stops offering it and a fresh start is possible.
  const archiveInterview = useArchiveInterview();
  const handleDiscard = async () => {
    if (!resumable) return;
    try {
      // Archive EVERY in-progress interview, not just the one surfaced in the
      // banner: duplicates created before interview-dedup landed re-trigger
      // resume detection one at a time, so a single archive looks like a no-op.
      for (const interview of inProgressInterviews?.data ?? []) {
        await archiveInterview.mutateAsync(interview.id);
      }
      toast("Interview discarded — you can start fresh.");
    } catch (error) {
      toast(
        error instanceof ApiError
          ? error.message
          : "Failed to discard the interview"
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>{children}</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {sendError && (
            <p role="alert" className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {sendError}
            </p>
          )}
          <RunWithChoice
            settings={aiSettings}
            kind={isExecution ? "execution" : "interview"}
            hasComputer={hasComputer}
            value={runWith}
            onChange={setRunWithChoice}
            offerCloud={offerCloud}
            cloudStatus={cloudStatus}
          />

          {executionKind === "orchestrate" && (
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={fastMode}
                onChange={(e) => { setFastMode(e.target.checked); }}
              />
              <span>
                <span className="block font-medium">Fast mode (faster output, higher cost)</span>
                <span className="block text-xs text-muted-foreground">
                  Same model, faster output, at a higher per-token price. Starts from this project&apos;s setting; if the account doesn&apos;t have fast mode, the run uses standard speed.
                </span>
              </span>
            </label>
          )}

          {executionKind === "orchestrate" && (
            <div className="space-y-1 text-sm">
              <label htmlFor="run-check-mode" className="block font-medium">
                Build checks
              </label>
              <select
                id="run-check-mode"
                className="w-full rounded-md border border-white/[0.08] bg-transparent px-2 py-1.5 text-sm"
                value={checkMode}
                onChange={(e) => { setCheckMode(toCheckMode(e.target.value)); }}
              >
                {CHECK_MODE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <span className="block text-xs text-muted-foreground">
                {CHECK_MODE_OPTIONS.find((o) => o.value === checkMode)?.hint} Starts from this project&apos;s setting.
              </span>
            </div>
          )}

          {executionOnKey && activeKey && (
            <div className="rounded-md border border-white/[0.08] p-3 text-sm text-muted-foreground">
              Your computer runs Claude Code with your {AI_PROVIDERS[activeKey.provider].name} key
              {aiSettings?.effectiveKey === "company" ? " (your company's key)" : ""} instead of its own sign-in, so
              this run is billed to that account. CompBox shows what it spent in Settings → AI.
            </div>
          )}

          {onCloud && cloudStatus && (
            <div className="rounded-md border border-white/[0.08] p-3 text-sm text-muted-foreground">
              A temporary CompBox machine {executionKind === "plan" ? "plans this" : "builds this"} with Claude Code on
              your Anthropic key{aiSettings?.effectiveKey === "company" ? " (your company's key)" : ""} and ends with a
              pull request. {Math.round(cloudHoursLeft(cloudStatus))} of {cloudStatus.includedHours} cloud hours left this
              month.
            </div>
          )}

          {onKey && activeKey && (
            <div className="rounded-md border border-white/[0.08] p-3 text-sm text-muted-foreground">
              The interview runs on CompBox using your {AI_PROVIDERS[activeKey.provider].name} key
              {aiSettings?.effectiveKey === "company" ? " (your company's key)" : ""}, so it uses that
              account&apos;s credits. You can see what it spent in Settings → AI.
            </div>
          )}

          {/* Seed banner (ENG-145): make it explicit that this launch carries a
              prior transcript as reference, not a duplicate of it. */}
          {seedFromInterview && (
            <div className="rounded-md border border-blue-800/30 bg-blue-900/[0.06] p-3 text-sm">
              <p className="text-blue-200">
                A previous transcript will be provided to the formulator as
                reference context for this new interview.
              </p>
            </div>
          )}
          {/* Resume (ENG-144): an in-progress interview exists — reconnect to
              its live job rather than starting a duplicate. */}
          {resumable && (
            <div className="rounded-md border border-purple-800/30 bg-purple-900/[0.06] p-3 text-sm">
              <div className="flex items-center gap-2 font-medium">
                <RotateCcw className="h-4 w-4 text-purple-300" />
                <span className="text-purple-200">
                  You have an interview in progress
                </span>
              </div>
              <p className="mt-1 text-muted-foreground">
                Pick up right where you left off — your previous answers are kept.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleResume}>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Resume interview
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  disabled={archiveInterview.isPending}
                  onClick={() => void handleDiscard()}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {archiveInterview.isPending ? "Discarding..." : "Discard"}
                </Button>
              </div>
            </div>
          )}

          {onKey || onCloud ? null : machinesLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading your machines...
            </div>
          ) : !machines || machines.length === 0 ? (
            <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground space-y-2">
              <div className="flex items-center gap-2 font-medium text-foreground">
                <Laptop className="h-4 w-4" />
                No paired machine yet
              </div>
              <p>
                Connect a computer first: follow the steps on{" "}
                <Link to="/connect" className="text-primary underline underline-offset-4">
                  Connect your computer
                </Link>{" "}
                on the machine you want to run agents on.
              </p>
            </div>
          ) : (
            <>
              {machines.length > 1 && (
                <div className="space-y-1.5">
                  <label className="text-sm font-medium leading-none">
                    Machine
                  </label>
                  <Select
                    value={selectedMachineId}
                    onValueChange={setSelectedMachineId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a machine..." />
                    </SelectTrigger>
                    <SelectContent>
                      {machines.map((machine) => {
                        const services = machineServicesRemote(
                          machine,
                          remoteFilter
                        );
                        return (
                          <SelectItem
                            key={machine.id}
                            value={machine.id}
                            disabled={!services}
                          >
                            <span className="flex items-center gap-2">
                              {machineReadiness(machine, Date.now(), jobType) === "ready" ? (
                                <Wifi className="h-3.5 w-3.5 text-green-400" />
                              ) : machineReadiness(machine, Date.now(), jobType) ===
                                "runtime-not-found" ? (
                                <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
                              ) : machineReadiness(machine, Date.now(), jobType) ===
                                "outdated" ? (
                                <ArrowUpCircle className="h-3.5 w-3.5 text-amber-400" />
                              ) : (
                                <WifiOff className="h-3.5 w-3.5 text-muted-foreground" />
                              )}
                              {machine.name}
                              {!services && (
                                <span className="text-xs text-muted-foreground">
                                  — repo not registered
                                </span>
                              )}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {isExecution && remoteFilter && !anyMachineServices && (
                <div className="rounded-md border border-amber-800/30 bg-amber-900/[0.06] p-3 text-sm">
                  <div className="flex items-center gap-2 font-medium text-amber-300">
                    <AlertTriangle className="h-4 w-4 text-amber-400" />
                    <span>No machine has this project&apos;s repo</span>
                  </div>
                  <p className="mt-1 text-muted-foreground">
                    None of your paired machines report a checkout of this
                    project&apos;s git remote. On the machine, register it with{" "}
                    <code className="text-xs">compbox machines dirs add</code>,
                    or set the team&apos;s git remote.
                  </p>
                </div>
              )}

              {selectedMachine && readiness === "ready" && (
                <div className="rounded-md border border-green-800/30 bg-green-900/[0.06] p-3 text-sm">
                  <div className="flex items-center gap-2 font-medium">
                    <Wifi className="h-4 w-4 text-green-400" />
                    <span className="text-green-300">
                      {selectedMachine.name} is ready
                    </span>
                  </div>
                  {selectedMachine.lastSeenAt && (
                    <p className="mt-1 text-muted-foreground">
                      Last check-in{" "}
                      {formatDistanceToNow(new Date(selectedMachine.lastSeenAt), {
                        addSuffix: true,
                      })}
                      .
                    </p>
                  )}
                </div>
              )}

              {/* Distinct "connected — agent runtime not found" state (ENG-139).
                  The daemon is alive but Claude Code isn't runnable — either it
                  isn't installed, or the agent is too old to report status. */}
              {selectedMachine && readiness === "runtime-not-found" && (
                <div className="rounded-md border border-amber-800/30 bg-amber-900/[0.06] p-3 text-sm">
                  <div className="flex items-center gap-2 font-medium">
                    <AlertTriangle className="h-4 w-4 text-amber-400" />
                    <span className="text-amber-300">
                      Agent runtime not found on {selectedMachine.name}
                    </span>
                  </div>
                  <p className="mt-1 text-muted-foreground">
                    This machine is connected but the Claude Code agent runtime
                    wasn&apos;t found. Install Claude Code (
                    <a
                      href="https://code.claude.com"
                      target="_blank"
                      rel="noreferrer"
                      className="underline decoration-dotted underline-offset-2 hover:text-foreground"
                    >
                      code.claude.com
                    </a>
                    ), or if it&apos;s already installed, update the CompBox
                    agent (
                    <code className="bg-muted px-1 py-0.5 rounded text-xs">
                      compbox run
                    </code>
                    ) so it can report status.
                  </p>
                  {selectedMachine.agentRuntimeDetail && (
                    <p className="mt-2 text-xs text-muted-foreground/80">
                      {/* Server-bounded (2000 chars); rendered as a text node so
                          React auto-escapes it — never dangerouslySetInnerHTML. */}
                      Detail: {selectedMachine.agentRuntimeDetail}
                    </p>
                  )}
                </div>
              )}

              {selectedMachine && readiness === "outdated" && (
                <OutdatedMachineNotice machine={selectedMachine} jobKind={jobType} />
              )}
              {selectedMachine && readiness === "offline" && (
                <div className="rounded-md border border-amber-800/30 bg-amber-900/[0.06] p-3 text-sm">
                  <div className="flex items-center gap-2 font-medium">
                    <WifiOff className="h-4 w-4 text-amber-400" />
                    <span className="text-amber-300">
                      Your CompBox session isn&apos;t running
                    </span>
                  </div>
                  <p className="mt-1 text-muted-foreground">
                    Start the daemon on <strong>{selectedMachine.name}</strong>{" "}
                    (
                    <code className="bg-muted px-1 py-0.5 rounded text-xs">
                      compbox run
                    </code>
                    ), then try again. This will update automatically once it
                    checks in.
                  </p>
                </div>
              )}
            </>
          )}

          {/* The chosen computer is offline: point at CompBox cloud (shown only when it can run). */}
          {isExecution && open && runWith === "computer" && hasComputer && readiness === "offline" && (
            <CloudRunOffer
              payload={payload}
              emphasis
              stay={!!onStarted}
              onStarted={(jobId) => {
                setOpen(false);
                onStarted?.(jobId);
              }}
            />
          )}

          {/* Deeper-interview roadmap (ENG-144): shows the full set of sections
              the machine-driven formulator will walk through, so the user sees
              the depth up front. Purely presentational — it does not change the
              job payload; the daemon (ENG-143) already asks the full set. Shown
              only for the interview flow; an execution dispatch (ENG-163) has no
              roadmap. */}
          {!isExecution && (
            <div className="max-h-64 overflow-y-auto rounded-lg border border-white/[0.06] p-3">
              <InterviewRoadmap />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => { setOpen(false); }}>
            Cancel
          </Button>
          <Button
            onClick={() => { void handleStart(); }}
            disabled={
              onKey
                ? startKeyInterview.isPending
                : onCloud
                  ? !cloudReady || startCloudRun.isPending
                  : !selectedMachineId ||
                  !ready ||
                  !selectedServicesTarget ||
                  enqueueJob.isPending ||
                  machinesLoading
            }
          >
            {enqueueJob.isPending || startKeyInterview.isPending || startCloudRun.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {pendingLabel}
              </>
            ) : onKey ? (
              "Start Interview"
            ) : onCloud ? (
              "Run in CompBox cloud"
            ) : (
              launchLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
